/*
 * MasteryResells theme — assets/theme.js
 * The single script for the theme. No libraries, no build step, no external requests.
 *
 * Behaviours (and nothing more):
 *   1. Floating header pill menu
 *   2. Add to cart  ([data-add-to-cart] + data-variant-id)
 *   3. Header cart count ([data-cart-count])
 *   4. Cart page quantity / remove ([data-cart-line])
 *   5. Product accordions (.product-accordion__trigger)
 *   6. Reveal-on-scroll animations (.reveal -> .is-visible)
 *   7. Product page form (form[data-product-form])
 *   8. PDP media thumbnails ([data-thumbnail] + [data-media-id])
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Constants + tiny helpers
   * ------------------------------------------------------------------ */

  var STATE_MS = 1600; // how long .in-cart / .in-cart-error / success text stay
  var DEBOUNCE_MS = 400; // cart quantity input debounce

  var CHECK_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"' +
    ' fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"' +
    ' stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<polyline points="20 6 9 17 4 12"></polyline></svg>';

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function closest(el, selector) {
    if (!el || el.nodeType !== 1) return null;
    if (typeof el.closest === 'function') return el.closest(selector);
    var node = el;
    while (node && node.nodeType === 1) {
      if (node.matches && node.matches(selector)) return node;
      node = node.parentElement;
    }
    return null;
  }

  function prefersReducedMotion() {
    if (!window.matchMedia) return false;
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    return !!(mq && mq.matches);
  }

  /* Read an error description out of whatever Shopify handed back. */
  function describeCartError(payload, fallback) {
    if (payload && typeof payload === 'object') {
      if (payload.description) return payload.description;
      if (payload.message) return payload.message;
      if (payload.errors) {
        if (typeof payload.errors === 'string') return payload.errors;
        try {
          return JSON.stringify(payload.errors);
        } catch (e) {
          /* ignore */
        }
      }
    }
    return fallback || 'Unable to update the cart.';
  }

  function parseJSONSafely(response) {
    return response.text().then(function (text) {
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch (e) {
        return null;
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * 3. Cart count
   * ------------------------------------------------------------------ */

  function writeCartCount(count) {
    var nodes = $$('[data-cart-count]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (!node) continue;
      node.textContent = String(count);
      node.style.display = count > 0 ? '' : 'none';
    }

    /* The badge is aria-hidden; the accessible name of the cart link comes from
     * [data-cart-count-text]. Keep it in sync or a screen reader keeps hearing
     * the count the page was rendered with. The singular / plural sentences are
     * rendered by Liquid from locales accessibility.cart_count, each holding the
     * example number Liquid interpolated — swap that number for the live one. */
    var labels = $$('[data-cart-count-text]');
    for (var j = 0; j < labels.length; j++) {
      var label = labels[j];
      var template = label.getAttribute(
        count === 1 ? 'data-cart-count-one' : 'data-cart-count-other'
      );
      if (!template) continue;
      label.textContent = template.replace(/\d+/, String(count));
    }
  }

  function refreshCartCount() {
    if (typeof window.fetch !== 'function') return Promise.resolve(null);
    return fetch('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) return null;
        return parseJSONSafely(response);
      })
      .then(function (cart) {
        if (!cart || typeof cart.item_count !== 'number') return null;
        writeCartCount(cart.item_count);
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  /* ------------------------------------------------------------------ *
   * 1. Header menu
   * ------------------------------------------------------------------ */

  function initHeaderMenu() {
    var header = $('.header-minimal');
    if (!header) return;

    var toggle = $('.header-minimal__toggle', header);
    if (!toggle) return;

    function isOpen() {
      return header.classList.contains('menu-open');
    }

    function setOpen(open) {
      header.classList.toggle('menu-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function close(returnFocus) {
      if (!isOpen()) return;
      setOpen(false);
      if (returnFocus && typeof toggle.focus === 'function') toggle.focus();
    }

    setOpen(false);

    toggle.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(!isOpen());
    });

    // Close when a menu link is followed.
    header.addEventListener('click', function (event) {
      var link = closest(event.target, '.header-minimal__menu-link');
      if (link) close(false);
    });

    // Close on outside click.
    document.addEventListener('click', function (event) {
      if (!isOpen()) return;
      if (header.contains(event.target)) return;
      close(false);
    });

    // Close on Escape and return focus to the toggle.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc' && event.keyCode !== 27) return;
      if (!isOpen()) return;
      close(true);
    });
  }

  /* ------------------------------------------------------------------ *
   * 2. Add to cart (delegated)
   * ------------------------------------------------------------------ */

  function restoreButton(button, originalHTML, stateClass) {
    if (!button) return;
    button.classList.remove(stateClass);
    if (typeof originalHTML === 'string') button.innerHTML = originalHTML;
    button.removeAttribute('aria-busy');
    button.disabled = false;
    delete button.dataset.cartBusy;
  }

  function flashButtonState(button, stateClass, originalHTML, swapIcon) {
    if (!button) return;
    button.classList.add(stateClass);
    if (swapIcon) button.innerHTML = CHECK_SVG;
    window.setTimeout(function () {
      restoreButton(button, originalHTML, stateClass);
    }, STATE_MS);
  }

  function addVariantToCart(button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    var id = Number(variantId);
    if (!id || isNaN(id)) return;

    if (button.dataset.cartBusy === 'true') return; // guard double-submits
    if (typeof window.fetch !== 'function') return;

    var originalHTML = button.innerHTML;
    button.dataset.cartBusy = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
    })
      .then(function (response) {
        return parseJSONSafely(response).then(function (payload) {
          if (!response.ok) {
            var error = new Error(describeCartError(payload, 'Add to cart failed.'));
            error.payload = payload;
            throw error;
          }
          return payload;
        });
      })
      .then(function () {
        // The button is usable again immediately; only the visual state lingers.
        button.disabled = false;
        button.removeAttribute('aria-busy');
        flashButtonState(button, 'in-cart', originalHTML, true);
        refreshCartCount();
      })
      .catch(function (error) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        flashButtonState(button, 'in-cart-error', originalHTML, false);
        if (window.console && typeof window.console.warn === 'function') {
          window.console.warn(
            '[cart] ' + describeCartError(error && error.payload, error && error.message)
          );
        }
      });
  }

  function initAddToCart() {
    document.addEventListener('click', function (event) {
      var button = closest(event.target, '[data-add-to-cart]');
      if (!button) return;

      // Controls inside a product form are handled by the form submit handler.
      if (closest(button, 'form[data-product-form]')) return;

      if (button.disabled || button.classList.contains('disabled')) return;

      event.preventDefault();
      addVariantToCart(button);
    });
  }

  /* ------------------------------------------------------------------ *
   * 4. Cart page quantity + remove
   * ------------------------------------------------------------------ */

  function changeCartLine(line, quantity) {
    if (typeof window.fetch !== 'function') return;
    if (!line) return;

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
    })
      .then(function (response) {
        return parseJSONSafely(response).then(function (payload) {
          if (!response.ok) {
            var error = new Error(describeCartError(payload, 'Cart update failed.'));
            error.payload = payload;
            throw error;
          }
          return payload;
        });
      })
      .then(function () {
        window.location.reload();
      })
      .catch(function (error) {
        if (window.console && typeof window.console.warn === 'function') {
          window.console.warn(
            '[cart] ' + describeCartError(error && error.payload, error && error.message)
          );
        }
        window.location.reload();
      });
  }

  function lineIndexFor(el) {
    if (!el) return null;
    var explicit = el.getAttribute('data-line');
    if (explicit) return explicit;
    var row = closest(el, '[data-cart-line]');
    if (!row) return null;
    return row.getAttribute('data-cart-line');
  }

  function initCartPage() {
    var rows = $$('[data-cart-line]');
    if (!rows.length) return;

    var timers = {};

    function scheduleChange(line, quantity) {
      if (!line) return;
      if (timers[line]) window.clearTimeout(timers[line]);
      timers[line] = window.setTimeout(function () {
        delete timers[line];
        changeCartLine(line, quantity);
      }, DEBOUNCE_MS);
    }

    document.addEventListener('click', function (event) {
      var remove = closest(event.target, '[data-cart-remove]');
      if (remove) {
        var removeLine = lineIndexFor(remove);
        if (!removeLine) return;
        event.preventDefault();
        changeCartLine(removeLine, 0);
        return;
      }

      var stepper = closest(event.target, '[data-quantity-minus], [data-quantity-plus], [data-quantity-change]');
      if (!stepper) return;

      var row = closest(stepper, '[data-cart-line]');
      if (!row) return;

      var line = lineIndexFor(stepper);
      if (!line) return;

      var input = $('[data-quantity-input]', row);
      var current = input ? parseInt(input.value, 10) : NaN;
      if (isNaN(current)) current = 1;

      var step = 0;
      if (stepper.hasAttribute('data-quantity-minus')) {
        step = -1;
      } else if (stepper.hasAttribute('data-quantity-plus')) {
        step = 1;
      } else {
        step = parseInt(stepper.getAttribute('data-quantity-change'), 10);
        if (isNaN(step)) step = 0;
      }
      if (step === 0) return;

      event.preventDefault();

      var next = current + step;
      if (next < 0) next = 0;
      if (input) input.value = String(next);
      scheduleChange(line, next);
    });

    document.addEventListener('change', function (event) {
      var input = closest(event.target, '[data-quantity-input]');
      if (!input) return;
      var line = lineIndexFor(input);
      if (!line) return;
      var value = parseInt(input.value, 10);
      if (isNaN(value) || value < 0) value = 0;
      input.value = String(value);
      scheduleChange(line, value);
    });

    document.addEventListener('input', function (event) {
      var input = closest(event.target, '[data-quantity-input]');
      if (!input) return;
      var line = lineIndexFor(input);
      if (!line) return;
      var value = parseInt(input.value, 10);
      if (isNaN(value)) return; // let the user finish typing
      if (value < 0) value = 0;
      scheduleChange(line, value);
    });
  }

  /* ------------------------------------------------------------------ *
   * 5. Product accordions
   * ------------------------------------------------------------------ */

  function initAccordions() {
    /* base.css only collapses the panels under html.js, so the markup ships
     * aria-expanded="true" (true for a no-JS visitor, who sees them open).
     * Once JS is running the panels really are collapsed — say so. */
    var triggers = $$('.product-accordion__trigger');
    for (var i = 0; i < triggers.length; i++) {
      var item = closest(triggers[i], '.product-accordion__item');
      var open = !!(item && item.classList.contains('is-open'));
      triggers[i].setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    document.addEventListener('click', function (event) {
      var trigger = closest(event.target, '.product-accordion__trigger');
      if (!trigger) return;

      var item = closest(trigger, '.product-accordion__item');
      if (!item) return;

      event.preventDefault();
      var open = !item.classList.contains('is-open');
      item.classList.toggle('is-open', open);
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------------------ *
   * 6. Reveal animations
   * ------------------------------------------------------------------ */

  function showAll(elements) {
    for (var i = 0; i < elements.length; i++) {
      elements[i].classList.add('is-visible');
    }
  }

  function initReveals() {
    var elements = $$('.reveal');
    if (!elements.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      showAll(elements);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    for (var i = 0; i < elements.length; i++) {
      observer.observe(elements[i]);
    }
  }

  /* ------------------------------------------------------------------ *
   * 7. Product page form
   * ------------------------------------------------------------------ */

  function showFormError(form, message) {
    var wrapper = $('.product-form__error-message-wrapper', form);
    var target = $('.product-form__error-message', form);
    if (target) target.textContent = message;
    if (wrapper) {
      wrapper.removeAttribute('hidden');
      wrapper.setAttribute('aria-hidden', 'false');
      wrapper.style.display = '';
    }
  }

  function hideFormError(form) {
    var wrapper = $('.product-form__error-message-wrapper', form);
    var target = $('.product-form__error-message', form);
    if (target) target.textContent = '';
    if (wrapper) {
      wrapper.setAttribute('hidden', 'hidden');
      wrapper.setAttribute('aria-hidden', 'true');
    }
  }

  function addedLabel(form, button) {
    if (button && button.getAttribute('data-added-text')) {
      return button.getAttribute('data-added-text');
    }
    if (form && form.getAttribute('data-added-text')) {
      return form.getAttribute('data-added-text');
    }
    return 'Added';
  }

  function initProductForm() {
    var forms = $$('form[data-product-form]');
    if (!forms.length) return;
    if (typeof window.fetch !== 'function') return; // let it post natively

    forms.forEach(function (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();

        var button =
          $('[type="submit"]', form) || $('.product-form__submit', form);

        if (form.dataset.cartBusy === 'true') return;
        form.dataset.cartBusy = 'true';

        var originalHTML = button ? button.innerHTML : null;
        if (button) {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        }
        hideFormError(form);

        var body;
        try {
          body = new FormData(form);
        } catch (e) {
          delete form.dataset.cartBusy;
          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }
          form.submit();
          return;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          body: body
        })
          .then(function (response) {
            return parseJSONSafely(response).then(function (payload) {
              if (!response.ok || (payload && payload.status)) {
                var error = new Error(describeCartError(payload, 'Add to cart failed.'));
                error.payload = payload;
                throw error;
              }
              return payload;
            });
          })
          .then(function () {
            delete form.dataset.cartBusy;
            if (button) {
              button.disabled = false;
              button.removeAttribute('aria-busy');
              button.classList.add('product-form__submit--success');
              button.textContent = addedLabel(form, button);
              window.setTimeout(function () {
                button.classList.remove('product-form__submit--success');
                if (typeof originalHTML === 'string') button.innerHTML = originalHTML;
              }, STATE_MS);
            }
            refreshCartCount();
          })
          .catch(function (error) {
            delete form.dataset.cartBusy;
            if (button) {
              button.disabled = false;
              button.removeAttribute('aria-busy');
              if (typeof originalHTML === 'string') button.innerHTML = originalHTML;
            }
            var message = describeCartError(error && error.payload, error && error.message);
            showFormError(form, message);
            if (window.console && typeof window.console.warn === 'function') {
              window.console.warn('[cart] ' + message);
            }
          });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 8. PDP media thumbnails
   * ------------------------------------------------------------------ */

  function initMediaThumbnails() {
    var thumbnails = $$('[data-thumbnail]');
    if (!thumbnails.length) return;

    var root = closest(thumbnails[0], '.product-page') || document;

    var items = $$('[data-media-id]', root).filter(function (node) {
      return !node.hasAttribute('data-thumbnail');
    });
    if (!items.length) return;

    function activate(mediaId) {
      if (!mediaId) return;

      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var active = item.getAttribute('data-media-id') === mediaId;
        item.classList.toggle('product__media-item--active', active);
        if (active) {
          item.removeAttribute('hidden');
        } else {
          item.setAttribute('hidden', 'hidden');

          // Stop audio from a video we are about to hide.
          var videos = $$('video', item);
          for (var v = 0; v < videos.length; v++) {
            if (typeof videos[v].pause === 'function') videos[v].pause();
          }
        }
      }

      for (var j = 0; j < thumbnails.length; j++) {
        var thumb = thumbnails[j];
        if (thumb.getAttribute('data-media-id') === mediaId) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      }
    }

    for (var k = 0; k < thumbnails.length; k++) {
      thumbnails[k].addEventListener('click', function (event) {
        var thumb = closest(event.target, '[data-thumbnail]');
        if (!thumb) return;
        event.preventDefault();
        activate(thumb.getAttribute('data-media-id'));
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  onReady(function () {
    initHeaderMenu();
    initAddToCart();
    initCartPage();
    initAccordions();
    initReveals();
    initProductForm();
    initMediaThumbnails();
    refreshCartCount();
  });

  // Expose only what other theme code may legitimately need.
  window.theme = window.theme || {};
  window.theme.refreshCartCount = refreshCartCount;
})();
